import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateAd, updateAdSet, updateCampaign } from '../../redux/metaAdsSlice';
import alertService from '../../utils/alert';
import { INPUT, Field, FormModal } from '../../components/crm/ui';

// Datetime-local wants "YYYY-MM-DDTHH:mm" in local time.
const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Shared save wrapper: runs the thunk, shows Meta's/the server's own message on failure, closes on success.
function useSave(onClose) {
  const [saving, setSaving] = useState(false);
  const run = async (thunkResultPromise, okMessage) => {
    setSaving(true);
    const result = await thunkResultPromise;
    setSaving(false);
    if (result.error) return alertService.error('Could not save', result.payload || 'Meta did not accept the change.');
    alertService.success('Saved', okMessage);
    onClose();
  };
  return { saving, run };
}

export function RenameCampaignDialog({ campaign, onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState(campaign.name || '');
  const { saving, run } = useSave(onClose);
  const submit = () => {
    if (!name.trim()) return alertService.error('Name needed', 'Give the campaign a name.');
    return run(dispatch(updateCampaign({ id: campaign._id, campaignData: { name: name.trim() } })), 'Campaign renamed.');
  };
  return (
    <FormModal title="Rename campaign" onClose={onClose} onSubmit={submit} saving={saving} size="max-w-md">
      <Field label="Campaign name"><input aria-label="Campaign name" className={INPUT} value={name} onChange={(e) => setName(e.target.value)} /></Field>
    </FormModal>
  );
}

export function EditAdSetDialog({ campaignId, adSet, campaignIsCbo, onClose }) {
  const dispatch = useDispatch();
  const usesLifetime = !!adSet.lifetimeBudget;
  const [form, setForm] = useState({
    name: adSet.name || '',
    budget: String(usesLifetime ? adSet.lifetimeBudget : (adSet.dailyBudget ?? '')),
    startTime: toLocalInput(adSet.startTime),
    endTime: toLocalInput(adSet.endTime),
    ageMin: String(adSet.targeting?.ageMin ?? 18),
    ageMax: String(adSet.targeting?.ageMax ?? 65),
  });
  const { saving, run } = useSave(onClose);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!form.name.trim()) return alertService.error('Name needed', 'Give the ad set a name.');
    const updates = { name: form.name.trim() };
    if (!campaignIsCbo && form.budget !== '') {
      const amount = Number(form.budget);
      if (!(amount > 0)) return alertService.error('Budget', 'Enter a budget greater than 0.');
      updates[usesLifetime ? 'lifetimeBudget' : 'dailyBudget'] = amount;
    }
    if (form.startTime) updates.startTime = new Date(form.startTime).toISOString();
    if (form.endTime) updates.endTime = new Date(form.endTime).toISOString();
    if (updates.startTime && updates.endTime && new Date(updates.endTime) <= new Date(updates.startTime)) {
      return alertService.error('Dates', 'The end date must be after the start date.');
    }
    const ageMin = Number(form.ageMin);
    const ageMax = Number(form.ageMax);
    if (ageMin < 13 || ageMax > 65 || ageMin > ageMax) return alertService.error('Age', 'Use an age range between 13 and 65, with the minimum not above the maximum.');
    if (ageMin !== (adSet.targeting?.ageMin ?? 18) || ageMax !== (adSet.targeting?.ageMax ?? 65)) {
      updates.targeting = { ageMin, ageMax };
    }
    return run(dispatch(updateAdSet({ campaignId, id: adSet._id, updates })), 'Ad set updated on Meta.');
  };

  return (
    <FormModal title={`Edit ${(adSet.name || '').trim()}`} onClose={onClose} onSubmit={submit} saving={saving} size="max-w-lg">
      <Field label="Ad set name"><input aria-label="Ad set name" className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      {campaignIsCbo ? (
        <p className="text-[11px] text-[#706E6B]">This campaign sets its budget on the campaign itself, so there is no budget to change here.</p>
      ) : (
        <Field label={usesLifetime ? 'Lifetime budget' : 'Daily budget'}>
          <input aria-label="Budget" type="number" min="1" className={INPUT} value={form.budget} onChange={(e) => set({ budget: e.target.value })} />
        </Field>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start"><input aria-label="Start" type="datetime-local" className={INPUT} value={form.startTime} onChange={(e) => set({ startTime: e.target.value })} /></Field>
        <Field label="End"><input aria-label="End" type="datetime-local" className={INPUT} value={form.endTime} onChange={(e) => set({ endTime: e.target.value })} /></Field>
        <Field label="Min age"><input aria-label="Min age" type="number" min="13" max="65" className={INPUT} value={form.ageMin} onChange={(e) => set({ ageMin: e.target.value })} /></Field>
        <Field label="Max age"><input aria-label="Max age" type="number" min="13" max="65" className={INPUT} value={form.ageMax} onChange={(e) => set({ ageMax: e.target.value })} /></Field>
      </div>
      <p className="text-[11px] text-[#706E6B]">Changing who the ad targets can restart Meta's learning phase, so results may dip for a few days.</p>
    </FormModal>
  );
}

export function EditAdDialog({ campaignId, ad, onClose }) {
  const dispatch = useDispatch();
  const c = ad.creative || {};
  const [form, setForm] = useState({
    name: ad.name || '', headline: c.headline || '', body: c.body || '', description: c.description || '', linkUrl: c.linkUrl || '',
  });
  const { saving, run } = useSave(onClose);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!form.name.trim()) return alertService.error('Name needed', 'Give the ad a name.');
    if (form.linkUrl && !/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(form.linkUrl.trim())) return alertService.error('Link', 'The website link must start with https://');
    const creativeChanged = ['headline', 'body', 'description', 'linkUrl'].some((k) => (form[k] || '') !== (c[k] || ''));
    const updates = { name: form.name.trim() };
    if (creativeChanged) updates.creative = { headline: form.headline, body: form.body, description: form.description, linkUrl: form.linkUrl.trim() };
    return run(dispatch(updateAd({ campaignId, id: ad._id, updates })), creativeChanged ? 'Ad updated. Meta may review it again before it keeps running.' : 'Ad renamed.');
  };

  return (
    <FormModal title={`Edit ${(ad.name || '').trim()}`} onClose={onClose} onSubmit={submit} saving={saving} size="max-w-lg">
      <Field label="Ad name"><input aria-label="Ad name" className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
      <Field label="Headline"><input aria-label="Headline" className={INPUT} value={form.headline} onChange={(e) => set({ headline: e.target.value })} /></Field>
      <Field label="Body text"><textarea aria-label="Body text" rows={3} className={INPUT} value={form.body} onChange={(e) => set({ body: e.target.value })} /></Field>
      <Field label="Description (optional)"><input aria-label="Description" className={INPUT} value={form.description} onChange={(e) => set({ description: e.target.value })} /></Field>
      <Field label="Website link"><input aria-label="Website link" className={INPUT} value={form.linkUrl} onChange={(e) => set({ linkUrl: e.target.value })} placeholder="https://yourwebsite.com" /></Field>
      <p className="text-[11px] text-[#706E6B]">Changing the text or link creates a new version of the ad on Meta, so it goes back through Meta's review.</p>
    </FormModal>
  );
}
